<template>
  <div
    class="owc-hotspot"
    :class="{
      'owc-hotspot--author': isEditMode && isContainerMode,
      'owc-hotspot--dark': isBackgroundDarkMode,
    }"
  >
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <SlotContainer
      v-if="!isEditMode && isContainerMode"
      refName="slot-hotspot"
      :slotName="activeHotspotId"
      :isReactive="true"
    >
      <slot :slot="activeHotspotId" :name="activeHotspotId"></slot>
    </SlotContainer>
    <SlotContainer
      v-else
      v-for="hotspotId in hotspotIds"
      :key="hotspotId"
      :slotName="hotspotId"
      refName="slot-hotspot-container"
      slot="hotspot-container"
      :isReactive="true"
    >
      <slot :slot="hotspotId" :name="hotspotId"></slot>
    </SlotContainer>
    <SlotContainer v-if="isEditMode" refName="slot-hotspot" slot="hotspot">
      <slot></slot>
    </SlotContainer>
    <div
      v-if="isContainerMode"
      class="owc-hotspot__buttons"
      :class="{ 'owc-hotspot__buttons--shown': areButtonsShown }"
      v-test-id.ignore="'buttons'"
    >
      <wb-button-group :direction="`${isMobile ? 'vertical' : 'horizontal'}`">
        <button
          ref="firstButton"
          :style="{ '--owc-hotspot__button-width': compareButtonLabel() }"
          class="wb-button wb-button--secondary wb-button--medium wb-button--semi-transparent owc-hotspot__button"
          :class="{
            // Dark mode: set button dark when inactive and light when active
            // Light mode: set button dark when active and light when inactive
            'wb-button--theme-dark': isDarkMode ? !isFirstHotspotActive : isFirstHotspotActive,
            'owc-hotspot__button--active': isFirstHotspotActive,
          }"
          @click="switchHotspot"
        >
          {{ firstButtonLabel }}
        </button>
        <button
          ref="secondButton"
          :style="{ '--owc-hotspot__button-width': compareButtonLabel() }"
          class="wb-button wb-button--secondary wb-button--medium wb-button--semi-transparent owc-hotspot__button"
          :class="{
            // Dark mode: set button dark when inactive and light when active
            // Light mode: set button dark when active and light when inactive
            'wb-button--theme-dark': isDarkMode ? isFirstHotspotActive : !isFirstHotspotActive,
            'owc-hotspot__button--active': !isFirstHotspotActive,
          }"
          @click="switchHotspot"
        >
          {{ secondButtonLabel }}
        </button>
      </wb-button-group>
    </div>
  </div>
</template>

<script lang="ts">
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { TestId } from '@owc-utils/TestIdDirective';
import { HotspotComponent } from './Hotspot.d';
import HotspotMixin from '@owc-utils/Mixins/HotspotMixin';
import { TrackingInstance } from '@owc-packages/Tracking/src/Tracking.d';
import initializeTracking, { formatTrackingString } from '@owc-packages/Tracking/src/TrackingUtils';
import { mobileBreakpoints } from '@owc-utils/MediaQueryService';

let trackingInstance: TrackingInstance | null = null;

export default (ComponentInitializeDispatcher as HotspotComponent).extend({
  name: 'owc-hotspot',
  mixins: [HotspotMixin],
  components: {
    SlotContainer,
    ValidationBoxWrapper,
  },
  directives: {
    TestId,
  },
  created() {
    this.onMQChange?.(this.isOnMobileView);
    this.addHotspotChangeListener(this.componentId, this.toggleButtonsVisibility);
  },
  beforeDestroy() {
    this.removeHotspotChangeListener(this.componentId, this.toggleButtonsVisibility);
  },
  data: () => ({
    activeHotspotId: '',
    areButtonsShown: true,
    isMobile: false,
  }),
  async mounted() {
    trackingInstance = await initializeTracking();
  },
  computed: {
    isContainerMode() {
      return Boolean(this.componentData?.containerMode);
    },
    isDarkMode() {
      return Boolean(this.componentData?.darkMode);
    },
    isBackgroundDarkMode() {
      return Boolean(this.componentData?.darkModeOverlayBackground);
    },
    isFirstHotspotActive() {
      return this.activeHotspotId === this.firstHotspotId;
    },
    firstHotspotId() {
      return this.hotspotIds[0] || '';
    },
    secondHotspotId() {
      return this.hotspotIds[1] || '';
    },
    hotspotIds() {
      return this.componentData?.hotspotIds || [];
    },
    firstButtonLabel() {
      return this.componentData?.firstButtonLabel || '';
    },
    secondButtonLabel() {
      return this.componentData?.secondButtonLabel || '';
    },
  },
  methods: {
    /**
     *
     * @compareButtonLabel
     * @description
     * @compareButtonLabel is a method to compare the length of two strings in @componentData.
     * The biggest length of both is the width of the buttons in the <wb-button-group>
     * @return 35ch as fallback
     * @return { string }  expected
     */
    compareButtonLabel(): string {
      let buttonLength = 35;

      if (this.componentData) {
        const maxLength = Math.max(this.firstButtonLabel.length, this.secondButtonLabel.length) + 1;
        buttonLength = Math.min(maxLength, buttonLength);
      }
      return `${this.isMobile ? '35' : buttonLength}ch`;
    },
    switchHotspot() {
      this.activeHotspotId = this.isFirstHotspotActive ? this.secondHotspotId : this.firstHotspotId;
      this.customTrackingTrigger();
    },
    toggleButtonsVisibility(state) {
      const { hiddenTabsHotspotIds } = state;
      if (this.isContainerMode) {
        this.areButtonsShown = !hiddenTabsHotspotIds.some(
          hotspotId =>
            this.activeHotspotId === hotspotId && this.componentData?.hotspotIds.includes(hotspotId),
        );
      }
    },
    customTrackingTrigger() {
      if (trackingInstance && this.componentData) {
        const isFirstButtonActive = this.isFirstHotspotActive;
        const tabEl = isFirstButtonActive ? this.$refs.firstButton : this.$refs.secondButton;
        const tabIndex = isFirstButtonActive ? 1 : 2;
        const tabLabel = isFirstButtonActive ? this.firstButtonLabel : this.secondButtonLabel;
        trackingInstance.customTrackingObjectUpdate(
          {
            label: `tab${tabIndex}.${formatTrackingString(tabLabel)}`,
            category: 'feature',
          },
          'click',
          tabEl,
        );
      }
    },
    isOnMobileView() {
      const currentMq = this.getCurrentMediaQuery?.() || '';
      this.isMobile = mobileBreakpoints.includes(currentMq);
    },
  },
  watch: {
    firstHotspotId(value: string) {
      if (value && !this.activeHotspotId) {
        this.activeHotspotId = value;
      }
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils';

$moduleName: 'owc-hotspot';

.#{$moduleName} {
  position: relative;

  width: 100%;

  overflow: hidden;

  @include breakpoint-from(mq3) {
    isolation: isolate;
  }

  &--author {
    padding-bottom: 64px;

    .owc-hotspot__buttons {
      bottom: 0;

      pointer-events: none;
    }
  }
  &--dark {
    background-color: var(--wb-black);
  }
  &__button {
    flex: 1;

    width: var(--owc-hotspot__button-width);

    white-space: nowrap;

    text-overflow: ellipsis;
  }
  &__buttons {
    position: absolute;
    bottom: calc(32px + var(--wb-spacing-xl));
    left: 50%;
    z-index: $z-index-content-lvl1;

    overflow: hidden;

    transform: translateX(-50%);

    opacity: 0;

    &--shown {
      opacity: 1;

      transition: opacity 1s $wb-fade;
    }
  }
  // Increase specificity of the selector
  .#{$moduleName} &__button {
    &--active {
      background-color: var(--wb-white);

      pointer-events: none;

      &.wb-button--theme-dark {
        background-color: var(--wb-black);
      }
    }
  }
}
</style>
